// Escaped-generation allocator — stage 2 of the memory-model rewrite
// (docs/memory-model.md §"Reclamation, staged").
//
// Stage 1 reclaims an event's transient heap by rewinding a bump cursor. What
// it can't reclaim is a heap object that OUTLIVES the event because a global
// holds it — and is then dropped when that global is reassigned. Those objects
// live in a separate "escaped" region that is never rewound; this allocator
// hands out and reclaims blocks within it.
//
// Layout of linear memory once both stages are in play:
//
//   [ static data | escaped generation | young generation ]
//                  ^ base              ^ young base (= escaped ceiling / limit)
//
// The escaped region holds BOTH the module's globals (bump-allocated at the low
// end during `init`) and the promoted objects the free list hands out above
// them. Because every persistent value lives here, the young region is genuinely
// EMPTY between events — so the escaped/young split can MOVE at runtime
// (docs/memory-model.md §"Follow-ons"): the host plants the young base a headroom
// above the globals after `init` (`setupEscapedAfterInit`) and RAISES it, growing
// the escaped region, at event boundaries under pressure (`growEscapedRegion`).
// Growth is sound only when young is empty, which is exactly the event boundary.
//
// Reclamation strategy: exact-size segregated free lists. `free` returns a
// block to the bin for its (4-byte-aligned) size; `alloc` reuses a block from
// that bin or bumps a fresh one. Reassigning a global to another value of the
// SAME type frees and re-allocates the same size, so the freed block is reused
// immediately — the common case costs nothing and the region stays flat. There
// is no coalescing or splitting yet: freeing many blocks of one size and then
// needing a different size leaves the first set idle (bounded, refinable later).
import { MunosResourceError, ESCAPED_HEAP_MAX_BYTES, ESCAPED_HEAP_INITIAL_HEADROOM, ESCAPED_HEAP_GROW_STEP, ESCAPED_HEAP_GROW_HEADROOM, } from "../limits.js";
function align4(bytes) {
    return (bytes + 3) & ~3;
}
// One escaped generation per member heap. Pure offset arithmetic over a
// [base, limit) window of the member's linear memory — it hands out addresses
// and never reads or writes the bytes, so it needs no view of the memory.
//
// Constructed with only its base (known at compile time); its cursor and limit
// are SEEDED after `init` has bump-allocated the globals (`seed`), because where
// the globals end — and therefore where the free list starts and the young base
// sits — is a runtime value. `grow` then raises the limit a step at a time up to
// base + ESCAPED_HEAP_MAX_BYTES.
export class EscapedHeap {
    base;
    cursor;
    lim;
    maxLimit;
    // Aligned byte size → stack of free block addresses of exactly that size.
    bins = new Map();
    // Live bytes handed out and not yet freed — for the flat-footprint assertion
    // in tests and for diagnostics.
    live = 0;
    constructor(base) {
        this.base = base;
        if (base % 4 !== 0) {
            throw new Error(`EscapedHeap base ${base} must be 4-byte aligned`);
        }
        // Unseeded: an empty [base, base) window until `seed` runs after init.
        this.cursor = base;
        this.lim = base;
        this.maxLimit = base + ESCAPED_HEAP_MAX_BYTES;
    }
    // Seed the region after `init` has bump-allocated the globals. `cursor0` is
    // where the globals end (the free list bumps promoted objects above it); the
    // globals in [base, cursor0) are pre-owned live state the free list never hands
    // out but a global reassignment CAN release back into it. `limit` is the young
    // base — the escaped ceiling — chosen a headroom above the globals.
    seed(cursor0, limit) {
        if (cursor0 < this.base || limit < cursor0) {
            throw new Error(`EscapedHeap.seed: bad window base=${this.base} cursor=${cursor0} limit=${limit}`);
        }
        this.cursor = cursor0;
        this.lim = limit;
        this.live = cursor0 - this.base; // the globals are live from the outset
    }
    // Current escaped/young split (the escaped ceiling, exclusive) — equals the
    // young base. The host reads it to know whether young is empty before growing.
    get limit() {
        return this.lim;
    }
    // Would a larger ceiling help? True when the high-water mark is within the
    // growth headroom of the limit and the limit isn't already at the maximum.
    needsGrowth() {
        return this.cursor + ESCAPED_HEAP_GROW_HEADROOM > this.lim && this.lim < this.maxLimit;
    }
    // Raise the ceiling by one growth step (capped at base + max) when under
    // pressure. Returns the new limit, or null if no growth happened (no pressure,
    // or already maxed). The caller (growEscapedRegion) must have ensured young is
    // empty, then commit memory to the new limit and lift the young base to it.
    grow() {
        if (!this.needsGrowth())
            return null;
        const next = Math.min(this.lim + ESCAPED_HEAP_GROW_STEP, this.maxLimit);
        if (next <= this.lim)
            return null;
        this.lim = next;
        return next;
    }
    // Allocate `bytes` in the escaped region. Reuses a same-size freed block if
    // one is available, else bumps the region's high-water mark. Throws if the
    // region is exhausted at its current ceiling — a single event cannot grow the
    // ceiling (young isn't empty mid-event), so this is a real "too much persistent
    // state promoted before the next boundary" signal, distinct from the young
    // heap's own ceiling. The initial headroom + boundary growth keep normal
    // workloads clear of it.
    alloc(bytes) {
        if (bytes < 0) {
            throw new MunosResourceError(`escaped alloc of negative size ${bytes}`);
        }
        const size = align4(bytes);
        const bin = this.bins.get(size);
        if (bin !== undefined && bin.length > 0) {
            this.live += size;
            return bin.pop();
        }
        const ptr = this.cursor;
        const next = ptr + size;
        if (next > this.lim) {
            throw new MunosResourceError(`munos: escaped-generation allocation of ${bytes} bytes (to ${next}) ` +
                `exceeds the escaped region ceiling ${this.lim}`);
        }
        this.cursor = next;
        this.live += size;
        return ptr;
    }
    // Return a block to its size bin. `bytes` MUST be the size the block was
    // allocated with — the caller (the refcount free-cascade) knows it from the
    // object header's capacity, so no per-block size metadata is stored here.
    free(ptr, bytes) {
        const size = align4(bytes);
        let bin = this.bins.get(size);
        if (bin === undefined) {
            bin = [];
            this.bins.set(size, bin);
        }
        bin.push(ptr);
        this.live -= size;
    }
    // Is `ptr` an address inside this escaped region? Promotion uses this to
    // tell an already-escaped value (alias it — just bump its refcount) from a
    // young value (copy it in). This is what makes global-to-global aliasing
    // fall out for free: storing an escaped object into a second global shares
    // the one object rather than duplicating it. A young value sits at or above
    // the limit (the young base), so this cleanly separates the two.
    containsPointer(ptr) {
        return ptr >= this.base && ptr < this.lim;
    }
    // Current high-water mark — the top of the escaped region actually touched
    // (globals + promoted). Flat across reassignment of same-typed globals; that
    // flatness is the stage-2 guarantee, the escaped analogue of stage 1's flat
    // young cursor.
    highWater() {
        return this.cursor;
    }
    // Bytes live (the pre-owned globals plus outstanding promoted objects).
    liveBytes() {
        return this.live;
    }
}
// Grow linear memory so `byteEnd` is committed, rebinding the host's cached view
// after each page grow (grow() detaches the previous ArrayBuffer).
function growMemoryTo(h, byteEnd) {
    while (byteEnd > h.memory.buffer.byteLength) {
        h.memory.grow(1);
        h.refreshView();
    }
}
// Round-trip helper: 4-byte-align an address up.
function align4up(n) {
    return (n + 3) & ~3;
}
// Establish the escaped/young split AFTER `init()` has bump-allocated the module's
// globals (docs/memory-model.md §"Follow-ons"). On entry the young cursor
// (heap_ptr) sits at the top of the globals; this reads it, plants the young base
// a headroom above, seeds the escaped heap over [base, youngBase), commits memory
// to the young base, and moves the young cursor there so the first event allocates
// scratch above the escaped region. Call once, between `init()` and the first event.
export function setupEscapedAfterInit(h, esc) {
    const globalsTop = h.heapPtrGlobal.value;
    const maxLimit = esc.base + ESCAPED_HEAP_MAX_BYTES;
    let youngBase = align4up(globalsTop + ESCAPED_HEAP_INITIAL_HEADROOM);
    if (youngBase > maxLimit) {
        // Globals + full headroom would exceed the escaped ceiling — clamp to the
        // ceiling, or (if the globals alone already exceed it) sit the young base
        // right above them with no growth room left.
        youngBase = Math.max(align4up(globalsTop), maxLimit);
    }
    esc.seed(globalsTop, youngBase);
    growMemoryTo(h, youngBase);
    h.heapPtrGlobal.value = youngBase;
}
// Raise the escaped ceiling at an event boundary when the region is under
// pressure (docs/memory-model.md §"Follow-ons"). Sound ONLY when young is empty —
// the caller invokes this right after a rewindable event's heapRewind, when the
// young cursor sits at the young base (= the escaped ceiling). If a non-rewindable
// event left young data above the base (cursor > limit), the split can't move
// without overwriting it, so this is a no-op there. When it grows, it commits the
// newly-claimed pages and lifts the young base to the new ceiling; the gap that
// was just-rewound young space folds into the escaped region, and escaped objects
// never move, so every outstanding escaped pointer stays valid.
export function growEscapedRegion(h, esc) {
    const cursor = h.heapPtrGlobal.value;
    if (cursor !== esc.limit)
        return; // young not empty at the split → unsafe to move
    const newLimit = esc.grow();
    if (newLimit === null)
        return;
    growMemoryTo(h, newLimit);
    h.heapPtrGlobal.value = newLimit;
}
//# sourceMappingURL=escaped-heap.js.map